import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api'
import { navigate } from '../router'
import { pushError, pushToast } from '../toast'
import { escapeHtml, sanitizeHtml, FILL_COLORS, TEXT_COLORS } from '../richText'
import type {
  CellStyle,
  ChecklistBlockBody,
  Client,
  FileBlockBody,
  Note,
  NoteBlock,
  NoteBlockKind,
  NoteSection,
  ShapeBlockBody,
  TableBlockBody,
} from '../../shared/types'

// A page is a set of containers at absolute coordinates, each holding ordinary
// flowed content — not a free-floating element canvas. Blocks snap to a grid,
// which is what keeps free placement from turning into a mess.
const GRID = 20
const MIN_WIDTH = 160

function snap(n: number): number {
  return Math.round(n / GRID) * GRID
}

function parseBody<T>(json: string, fallback: T): T {
  try {
    return { ...fallback, ...(JSON.parse(json) as T) }
  } catch {
    return fallback
  }
}

/** Text blocks were plain strings before rich text; older ones are promoted to
 *  escaped HTML on read so nothing needs migrating. */
function textHtml(json: string): string {
  const body = parseBody<{ html?: string; text?: string }>(json, {})
  if (typeof body.html === 'string') return body.html
  return body.text ? escapeHtml(body.text) : ''
}

const NEW_BLOCK: Record<NoteBlockKind, { width: number; body: unknown }> = {
  text: { width: 320, body: { html: '' } },
  checklist: { width: 320, body: { items: [{ text: '', done: false }] } satisfies ChecklistBlockBody },
  table: { width: 420, body: { rows: [['', ''], ['', '']], header: true } satisfies TableBlockBody },
  // Created by upload, never by the palette — the entry exists so the record
  // stays exhaustive over NoteBlockKind.
  file: { width: 320, body: {} },
  shape: { width: 360, body: { shape: 'rect', height: 160 } satisfies ShapeBlockBody },
  image: { width: 320, body: {} },
  chart: { width: 420, body: {} },
}

export default function Notes({ noteId, clientScope }: { noteId?: number; clientScope?: number }) {
  const [clients, setClients] = useState<Client[]>([])
  const [scope, setScope] = useState<number | 'general'>(clientScope ?? 'general')
  const [sections, setSections] = useState<NoteSection[]>([])
  const [activeSection, setActiveSection] = useState<number | null>(null)
  const [notes, setNotes] = useState<Note[]>([])
  const [loading, setLoading] = useState(true)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [pagesOpen, setPagesOpen] = useState(true)
  const [allSections, setAllSections] = useState<NoteSection[]>([])

  // Every section across every client — the target list for moving a page.
  const loadAllSections = useCallback(async () => {
    try {
      setAllSections(await api.listAllSections())
    } catch {
      /* move targets are a convenience; a failure shouldn't break the page */
    }
  }, [])
  useEffect(() => {
    void loadAllSections()
  }, [loadAllSections])

  useEffect(() => {
    api.listClients(false).then(setClients).catch(() => {})
  }, [])

  // Arriving via #/client/:id/notes scopes the screen to that client.
  useEffect(() => {
    if (clientScope != null) setScope(clientScope)
  }, [clientScope])

  const loadSections = useCallback(async () => {
    setLoading(true)
    try {
      const rows = await api.listSections(scope)
      setSections(rows)
      setActiveSection((cur) => (rows.some((s) => s.id === cur) ? cur : rows[0]?.id ?? null))
    } catch (e) {
      pushError(`Couldn't load sections: ${(e as Error).message}`)
    } finally {
      setLoading(false)
    }
  }, [scope])

  useEffect(() => {
    void loadSections()
  }, [loadSections])

  const loadNotes = useCallback(async () => {
    if (activeSection == null) {
      setNotes([])
      return
    }
    try {
      setNotes(await api.listNotes({ section: activeSection }))
    } catch (e) {
      pushError(`Couldn't load pages: ${(e as Error).message}`)
    }
  }, [activeSection])

  useEffect(() => {
    void loadNotes()
  }, [loadNotes])

  async function addSection() {
    const name = prompt('Section name')
    if (!name) return
    const created = await api.createSection({ name, client_id: scope === 'general' ? null : scope })
    setSections((s) => [...s, created])
    setActiveSection(created.id)
  }

  async function renameSection(s: NoteSection, name: string) {
    const updated = await api.updateSection(s.id, { name })
    setSections((list) => list.map((x) => (x.id === s.id ? updated : x)))
  }

  async function removeSection(s: NoteSection) {
    if (!confirm(`Delete section "${s.name}" and every page in it? This cannot be undone.`)) return
    await api.deleteSection(s.id)
    setSections((list) => list.filter((x) => x.id !== s.id))
    setActiveSection((cur) => (cur === s.id ? null : cur))
  }

  async function addNote() {
    if (activeSection == null) return
    // The server copies the section's template page when one is set.
    const { note } = await api.createNote({ section_id: activeSection, title: 'Untitled' })
    await loadNotes()
    navigate(`#/notes/${note.id}`)
  }

  async function setTemplate(noteIdOrNull: number | null) {
    if (activeSection == null) return
    const updated = await api.updateSection(activeSection, { default_note_id: noteIdOrNull })
    setSections((list) => list.map((s) => (s.id === activeSection ? updated : s)))
  }

  async function moveSectionToClient(clientId: number | null) {
    if (activeSection == null) return
    await api.updateSection(activeSection, { client_id: clientId })
    await loadSections()
    await loadAllSections()
  }

  const activeNote = noteId ?? notes[0]?.id

  return (
    <div className="relative mx-auto max-w-[100rem] px-4 py-5 sm:px-6 lg:px-8">
      {/* Client scope: colour-coded initials rather than a dropdown */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <ScopeChip
          label="GEN"
          title="General — not tied to a client"
          color="#94a3b8"
          active={scope === 'general'}
          onClick={() => setScope('general')}
        />
        {clients.map((c) => (
          <ScopeChip
            key={c.id}
            label={initialsFor(c)}
            title={c.simple_name}
            color={c.accent_color ?? '#3b6ea5'}
            active={scope === c.id}
            onClick={() => setScope(c.id)}
          />
        ))}
      </div>

      {/* Section tabs — double-click a tab to rename it */}
      <div className="mb-4 flex flex-wrap items-center gap-1 border-b border-slate-200">
        {sections.map((s) => (
          <SectionTab
            key={s.id}
            section={s}
            active={s.id === activeSection}
            clients={clients}
            onSelect={() => setActiveSection(s.id)}
            onRename={(name) => renameSection(s, name)}
            onDelete={() => removeSection(s)}
            onMoveToClient={moveSectionToClient}
          />
        ))}
        <button className="px-2 py-1.5 text-sm text-[var(--accent)] hover:underline" onClick={addSection}>
          + Section
        </button>
      </div>

      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : activeSection == null ? (
        <p className="text-slate-400">No sections.</p>
      ) : (
        <div className={'grid grid-cols-1 gap-6 ' + (pagesOpen ? 'lg:grid-cols-[14rem_1fr]' : 'lg:grid-cols-[2rem_1fr]')}>
          <aside>
            <div className="mb-1 flex items-center justify-between">
              <button
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                title={pagesOpen ? 'Collapse pages' : 'Expand pages'}
                aria-label={pagesOpen ? 'Collapse pages' : 'Expand pages'}
                aria-expanded={pagesOpen}
                onClick={() => setPagesOpen((v) => !v)}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                  {pagesOpen ? <path d="M10 3.5 5.5 8l4.5 4.5" /> : <path d="M6 3.5 10.5 8 6 12.5" />}
                </svg>
              </button>
              {pagesOpen && (
                <button className="text-xs text-[var(--accent)] hover:underline" onClick={addNote}>
                  + Page
                </button>
              )}
            </div>
            {pagesOpen && (
              <ul className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {notes.map((n) => (
                  <li key={n.id} className="border-b border-slate-100 last:border-0">
                    <PageRow
                      note={n}
                      active={n.id === activeNote}
                      onSelect={() => navigate(`#/notes/${n.id}`)}
                      onRename={async (title) => {
                        await api.updateNote(n.id, { title })
                        await loadNotes()
                      }}
                      onArchive={async () => {
                        await api.updateNote(n.id, { archived: 1 })
                        await loadNotes()
                        pushToast('Page archived', {
                          actionLabel: 'Undo',
                          onAction: async () => {
                            await api.updateNote(n.id, { archived: 0 })
                            await loadNotes()
                          },
                        })
                        if (n.id === activeNote) navigate('#/notes')
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {activeNote != null && (
            <NotePage
              key={activeNote}
              noteId={activeNote}
              clients={clients}
              allSections={allSections}
              isTemplate={sections.find((s) => s.id === activeSection)?.default_note_id === activeNote}
              onSetTemplate={setTemplate}
              onMetaChanged={loadNotes}
            />
          )}
        </div>
      )}

      <button
        className="fixed bottom-5 right-5 z-30 rounded-full border border-slate-200 bg-white p-2.5 text-slate-400 shadow-lg hover:text-slate-700"
        title="Archived pages"
        aria-label="Archived pages"
        onClick={() => setArchiveOpen(true)}
      >
        <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="1.5" y="2.5" width="13" height="3" rx="0.5" />
          <path d="M2.5 5.5v7a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-7" />
          <line x1="6" y1="8.5" x2="10" y2="8.5" />
        </svg>
      </button>

      {archiveOpen && <ArchiveModal onClose={() => setArchiveOpen(false)} onRestored={loadNotes} />}
    </div>
  )
}

// ── small pieces ────────────────────────────────────────────────────────────

/** First letter of each word — one letter for a single-word name. Splitting on
 *  whitespace rather than all punctuation keeps "Hosea's Heart" at HH. Names
 *  can collide (Comply and Corvex are both C); colour and tooltip disambiguate. */
function initialsFor(client: Client): string {
  return client.simple_name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

function ScopeChip({
  label,
  title,
  color,
  active,
  onClick,
}: {
  label: string
  title: string
  color: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onClick}
      className={
        'flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-xs font-semibold tracking-tight transition ' +
        (active ? 'text-white' : 'text-slate-600 hover:bg-slate-100')
      }
      style={
        active
          ? { backgroundColor: color }
          : { border: `2px solid ${color}`, backgroundColor: 'transparent' }
      }
    >
      {label}
    </button>
  )
}

function SectionTab({
  section,
  active,
  clients,
  onSelect,
  onRename,
  onDelete,
  onMoveToClient,
}: {
  section: NoteSection
  active: boolean
  clients: Client[]
  onSelect: () => void
  onRename: (name: string) => void
  onDelete: () => void
  onMoveToClient: (clientId: number | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(section.name)
  const [menu, setMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menu) return
    const away = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(false)
    }
    window.addEventListener('mousedown', away)
    return () => window.removeEventListener('mousedown', away)
  }, [menu])

  const commit = () => {
    setEditing(false)
    const next = draft.trim()
    if (next && next !== section.name) onRename(next)
    else setDraft(section.name)
  }

  const cls =
    'rounded-t-md px-3 py-1.5 text-sm ' +
    (active ? 'border-x border-t border-slate-200 bg-white font-medium text-slate-900' : 'text-slate-500 hover:bg-slate-100')

  return (
    <div className="group relative">
      {editing ? (
        <input
          autoFocus
          className={cls + ' w-32 outline-none'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit()
            if (e.key === 'Escape') {
              setDraft(section.name)
              setEditing(false)
            }
          }}
        />
      ) : (
        <button
          className={cls}
          style={section.color ? { borderBottom: `2px solid ${section.color}` } : undefined}
          onClick={onSelect}
          onDoubleClick={() => {
            setDraft(section.name)
            setEditing(true)
          }}
        >
          {section.name}
        </button>
      )}
      {active && !editing && (
        // Visible by default so it's reachable on touch; hover-gated only on
        // desktop, where hover exists.
        <div className="absolute -right-1 top-0 block lg:hidden lg:group-hover:block" ref={menuRef}>
          <button
            className="px-1 text-slate-400 hover:text-slate-700"
            title="Section options"
            aria-label="Section options"
            onClick={() => setMenu((v) => !v)}
          >
            ⋯
          </button>
          {menu && (
            <div className="absolute right-0 top-full z-30 mt-1 w-44 rounded-md border border-slate-200 bg-white py-1 text-xs shadow-lg">
              <button
                className="block w-full px-3 py-1.5 text-left text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  setMenu(false)
                  setDraft(section.name)
                  setEditing(true)
                }}
              >
                Rename
              </button>
              <label className="block px-3 py-1.5 text-slate-700">
                Move to
                <select
                  className="mt-1 w-full rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px]"
                  value={section.client_id == null ? 'general' : String(section.client_id)}
                  onChange={(e) => {
                    setMenu(false)
                    onMoveToClient(e.target.value === 'general' ? null : Number(e.target.value))
                  }}
                >
                  <option value="general">General</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.simple_name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="my-1 border-t border-slate-100" />
              <button
                className="block w-full px-3 py-1.5 text-left text-red-600 hover:bg-red-50"
                onClick={() => {
                  setMenu(false)
                  onDelete()
                }}
              >
                Delete section
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PageRow({
  note,
  active,
  onSelect,
  onRename,
  onArchive,
}: {
  note: Note
  active: boolean
  onSelect: () => void
  onRename: (title: string) => void
  onArchive: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(note.title)

  const commit = () => {
    setEditing(false)
    const next = draft.trim()
    if (next && next !== note.title) onRename(next)
    else setDraft(note.title)
  }

  if (editing) {
    return (
      <input
        autoFocus
        className="w-full bg-white px-3 py-2 text-sm outline-none"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') {
            setDraft(note.title)
            setEditing(false)
          }
        }}
      />
    )
  }

  return (
    <div
      className={
        'group/row flex items-center ' +
        (active ? 'bg-slate-100' : 'hover:bg-slate-50')
      }
    >
      <button
        className={
          'flex min-w-0 flex-1 items-center gap-1.5 px-3 py-2 text-left text-sm ' +
          (active ? 'font-medium text-slate-900' : 'text-slate-600')
        }
        onClick={onSelect}
        onDoubleClick={() => {
          setDraft(note.title)
          setEditing(true)
        }}
        title="Double-click to rename"
      >
        {note.pinned_to_context === 1 && <PinIcon filled className="shrink-0 text-[var(--accent)]" />}
        <span className="truncate">{note.title || 'Untitled'}</span>
      </button>
      {/* Always visible on touch; hover-gated on desktop only. */}
      <button
        className="px-2 py-2 text-slate-300 hover:text-red-500 lg:opacity-0 lg:group-hover/row:opacity-100"
        title="Archive page"
        aria-label={`Archive ${note.title || 'Untitled'}`}
        onClick={onArchive}
      >
        ✕
      </button>
    </div>
  )
}

function PinIcon({ filled, className }: { filled?: boolean; className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      className={className}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <path d="M6 1.5h4l-.5 4 2.5 2.5H4L6.5 5.5z" />
      <line x1="8" y1="8" x2="8" y2="14.5" />
    </svg>
  )
}

// ── one page: the positioned-block canvas ───────────────────────────────────

function NotePage({
  noteId,
  clients,
  allSections,
  isTemplate,
  onSetTemplate,
  onMetaChanged,
}: {
  noteId: number
  clients: Client[]
  allSections: NoteSection[]
  isTemplate: boolean
  onSetTemplate: (noteId: number | null) => void
  onMetaChanged: () => void
}) {
  const [note, setNote] = useState<Note | null>(null)
  const [blocks, setBlocks] = useState<NoteBlock[]>([])
  const [drag, setDrag] = useState<{ id: number; x: number; y: number } | null>(null)
  const [saving, setSaving] = useState(0)
  const canvasRef = useRef<HTMLDivElement>(null)

  // What the page-level formatting bar is currently acting on.
  const [focusId, setFocusId] = useState<number | null>(null)
  const [richFocused, setRichFocused] = useState(false)
  const [cellSel, setCellSel] = useState<{ r: number; c: number } | null>(null)
  const [tableFilters, setTableFilters] = useState<Record<number, boolean>>({})

  const focusBlock = (id: number) => {
    setFocusId((prev) => {
      if (prev !== id) setCellSel(null)
      return id
    })
  }

  useEffect(() => {
    let cancelled = false
    api
      .getNote(noteId)
      .then(({ note, blocks }) => {
        if (cancelled) return
        setNote(note)
        setBlocks(blocks)
      })
      .catch((e) => pushError(`Couldn't load page: ${(e as Error).message}`))
    return () => {
      cancelled = true
    }
  }, [noteId])

  // Debounced per-block save. Same shape as useAutosave: merge rapid edits,
  // flush ~400ms after the last one, re-queue on failure rather than dropping.
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const pending = useRef<Map<number, Partial<NoteBlock>>>(new Map())

  const flush = useCallback(async (id: number) => {
    const patch = pending.current.get(id)
    if (!patch) return
    pending.current.delete(id)
    setSaving((n) => n + 1)
    try {
      await api.updateBlock(id, patch)
    } catch (e) {
      pending.current.set(id, { ...patch, ...(pending.current.get(id) ?? {}) })
      pushError(`Save failed — your last change is not saved. ${(e as Error).message}`)
    } finally {
      setSaving((n) => n - 1)
    }
  }, [])

  const scheduleSave = useCallback(
    (id: number, patch: Partial<NoteBlock>) => {
      pending.current.set(id, { ...(pending.current.get(id) ?? {}), ...patch })
      const t = timers.current.get(id)
      if (t) clearTimeout(t)
      timers.current.set(id, setTimeout(() => void flush(id), 400))
    },
    [flush],
  )

  useEffect(
    () => () => {
      for (const id of [...pending.current.keys()]) void flush(id)
    },
    [flush],
  )

  const patchBlock = useCallback(
    (id: number, patch: Partial<NoteBlock>, persist = true) => {
      setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)))
      if (persist) scheduleSave(id, patch)
    },
    [scheduleSave],
  )

  const setBody = useCallback(
    (id: number, body: unknown) => patchBlock(id, { body_json: JSON.stringify(body) }),
    [patchBlock],
  )

  async function addBlock(kind: NoteBlockKind, x?: number, y?: number) {
    const spec = NEW_BLOCK[kind]
    // Dropped below existing content when added from the toolbar rather than
    // by clicking a specific spot.
    const fallbackY = blocks.length ? Math.max(...blocks.map((b) => b.y)) + 120 : 0
    // Shapes are backdrops: they go behind everything so they can group blocks
    // visually without covering them.
    const z = kind === 'shape' ? Math.min(0, ...blocks.map((b) => b.z_index)) - 1 : blocks.length
    try {
      const created = await api.createBlock(noteId, {
        kind,
        x: snap(x ?? 0),
        y: snap(y ?? fallbackY),
        width: spec.width,
        z_index: z,
        body_json: JSON.stringify(spec.body),
      })
      setBlocks((bs) => [...bs, created])
    } catch (e) {
      pushError(`Couldn't add block: ${(e as Error).message}`)
    }
  }

  async function attachFile(file: File) {
    const fallbackY = blocks.length ? Math.max(...blocks.map((b) => b.y)) + 120 : 0
    try {
      const { block } = await api.uploadAttachment(noteId, file, { x: 0, y: snap(fallbackY) })
      setBlocks((bs) => [...bs, block])
    } catch (e) {
      pushError(`Upload failed: ${(e as Error).message}`)
    }
  }

  async function removeBlock(id: number) {
    const snapshot = blocks.find((b) => b.id === id)
    setBlocks((bs) => bs.filter((b) => b.id !== id))
    try {
      await api.deleteBlock(id)
      pushToast('Block deleted', {
        actionLabel: 'Undo',
        onAction: async () => {
          if (!snapshot) return
          const restored = await api.createBlock(noteId, {
            kind: snapshot.kind,
            x: snapshot.x,
            y: snapshot.y,
            width: snapshot.width,
            z_index: snapshot.z_index,
            body_json: snapshot.body_json,
          })
          setBlocks((bs) => [...bs, restored])
        },
      })
    } catch (e) {
      if (snapshot) setBlocks((bs) => [...bs, snapshot])
      pushError(`Delete failed: ${(e as Error).message}`)
    }
  }

  // Click empty canvas to create a container there — the OneNote gesture.
  function onCanvasMouseDown(e: React.MouseEvent) {
    if (e.target !== canvasRef.current) return
    const rect = canvasRef.current!.getBoundingClientRect()
    void addBlock('text', e.clientX - rect.left, e.clientY - rect.top)
  }

  function startDrag(e: React.MouseEvent, block: NoteBlock) {
    e.stopPropagation()
    const originX = block.x
    const originY = block.y
    const anchorX = e.clientX
    const anchorY = e.clientY
    let moved = false
    const topZ = Math.max(0, ...blocks.map((b) => b.z_index)) + 1

    const move = (ev: MouseEvent) => {
      const dx = ev.clientX - anchorX
      const dy = ev.clientY - anchorY
      if (!moved && Math.abs(dx) + Math.abs(dy) < 4) return
      moved = true
      setDrag({ id: block.id, x: Math.max(0, snap(originX + dx)), y: Math.max(0, snap(originY + dy)) })
    }
    const up = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      setDrag(null)
      if (!moved) return
      patchBlock(block.id, {
        x: Math.max(0, snap(originX + ev.clientX - anchorX)),
        y: Math.max(0, snap(originY + ev.clientY - anchorY)),
        z_index: topZ,
      })
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  function startResize(e: React.MouseEvent, block: NoteBlock) {
    e.stopPropagation()
    e.preventDefault()
    const startX = e.clientX
    const startW = block.width
    const move = (ev: MouseEvent) => {
      const w = Math.max(MIN_WIDTH, snap(startW + ev.clientX - startX))
      setBlocks((bs) => bs.map((b) => (b.id === block.id ? { ...b, width: w } : b)))
    }
    const up = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      patchBlock(block.id, { width: Math.max(MIN_WIDTH, snap(startW + ev.clientX - startX)) })
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  // The canvas grows in both directions: whatever is furthest out, plus a
  // screenful of slack so there is always somewhere to drop the next block.
  const canvasHeight = useMemo(() => Math.max(600, ...blocks.map((b) => b.y + 360)), [blocks])
  const canvasWidth = useMemo(
    () => Math.max(1000, ...blocks.map((b) => b.x + b.width + 400)),
    [blocks],
  )

  async function archivePage() {
    if (!note) return
    await api.updateNote(note.id, { archived: 1 })
    onMetaChanged()
    pushToast('Page archived', {
      actionLabel: 'Undo',
      onAction: async () => {
        await api.updateNote(note.id, { archived: 0 })
        onMetaChanged()
      },
    })
    navigate('#/notes')
  }

  if (!note) return <p className="text-slate-400">Loading page…</p>

  return (
    <div>
      {/* Desktop keeps the page title; on mobile it duplicates the page list
          and costs vertical space that matters more there. */}
      <input
        className="mb-1 hidden w-full border-0 bg-transparent text-xl font-semibold text-slate-900 outline-none lg:block"
        value={note.title}
        placeholder="Untitled"
        onChange={(e) => setNote({ ...note, title: e.target.value })}
        onBlur={async () => {
          await api.updateNote(note.id, { title: note.title })
          onMetaChanged()
        }}
      />

      <div className="mb-2 flex items-center justify-between gap-2">
        {/* Compact on phones, where a row of icons would crowd the header. */}
        <span className="lg:hidden">
          <AddBlockMenu onAdd={addBlock} onAttach={attachFile} />
        </span>
        <span className="hidden lg:block" />

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={saving > 0 ? 'text-slate-400' : 'text-slate-300'}>{saving > 0 ? 'Saving…' : 'Saved'}</span>

          <button
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            title="Duplicate this page"
            aria-label="Duplicate this page"
            onClick={async () => {
              const { note: copy } = await api.duplicateNote(note.id)
              onMetaChanged()
              navigate(`#/notes/${copy.id}`)
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
              <rect x="5.5" y="5.5" width="9" height="9" rx="1" />
              <path d="M10.5 5.5v-3a1 1 0 0 0-1-1h-7a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h3" />
            </svg>
          </button>

          <button
            className={
              'rounded p-1.5 ' +
              (isTemplate ? 'bg-slate-100 text-[var(--accent)]' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700')
            }
            title={
              isTemplate
                ? 'Template for this section — new pages start as a copy of this one'
                : 'Use as the template for new pages in this section'
            }
            aria-pressed={isTemplate}
            onClick={() => onSetTemplate(isTemplate ? null : note.id)}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill={isTemplate ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.3">
              <path d="M8 1.6l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.4l-3.8 2 .7-4.3-3.1-3 4.3-.6z" />
            </svg>
          </button>

          <button
            className={
              'rounded p-1.5 ' +
              (note.pinned_to_context === 1
                ? 'bg-slate-100 text-[var(--accent)]'
                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700')
            }
            title={
              note.pinned_to_context === 1
                ? "Pinned — always in the assistant's context for this client"
                : "Pin to the assistant's context"
            }
            aria-pressed={note.pinned_to_context === 1}
            onClick={async () => {
              const v = note.pinned_to_context === 1 ? 0 : 1
              setNote({ ...note, pinned_to_context: v })
              await api.updateNote(note.id, { pinned_to_context: v })
              onMetaChanged()
            }}
          >
            <PinIcon filled={note.pinned_to_context === 1} />
          </button>

          <PageMenu
            note={note}
            clients={clients}
            allSections={allSections}
            onArchive={archivePage}
            onSetClient={async (v) => {
              setNote({ ...note, client_id: v })
              await api.updateNote(note.id, { client_id: v })
              onMetaChanged()
            }}
            onMoveSection={async (sectionId) => {
              await api.updateNote(note.id, { section_id: sectionId })
              onMetaChanged()
              navigate('#/notes')
            }}
          />
        </div>
      </div>

      {/* Desktop: element types spelled out above the format bar. */}
      <div className="mb-1 hidden lg:block">
        <AddBlockBar onAdd={addBlock} onAttach={attachFile} />
      </div>

      <div className="mb-2">
        <FormatBar
          block={blocks.find((b) => b.id === focusId) ?? null}
          richFocused={richFocused}
          cell={cellSel}
          filtersOn={focusId != null && tableFilters[focusId] === true}
          onToggleFilters={(v) => focusId != null && setTableFilters((f) => ({ ...f, [focusId]: v }))}
          onChangeBody={(body) => focusId != null && setBody(focusId, body)}
        />
      </div>

      {/* Scroll container holds the viewport; the inner canvas is the full
          extent of the content, so it grows rightward the same way it grows
          downward. */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <div
        ref={canvasRef}
        onMouseDown={onCanvasMouseDown}
        // `isolate` matters: shapes sit at a negative z-index so they render
        // behind other blocks. Without a stacking context here they'd escape
        // to an ancestor and paint behind its white background — invisible.
        className="relative isolate"
        style={{
          height: canvasHeight,
          width: canvasWidth,
          backgroundImage: 'radial-gradient(#e2e8f0 1px, transparent 1px)',
          backgroundSize: `${GRID}px ${GRID}px`,
        }}
      >
        {blocks.map((b) => {
          const moving = drag?.id === b.id
          return (
            <div
              key={b.id}
              className={
                'group absolute rounded ' +
                // A shape is the content, not something in a card — no white
                // fill or border of its own.
                (b.kind === 'shape' ? '' : 'border bg-white ') +
                (moving
                  ? 'border-[var(--accent)] shadow-lg'
                  : focusId === b.id
                    ? 'border-[var(--accent)] ' + (b.kind === 'shape' ? 'ring-1 ring-[var(--accent)]' : '')
                    : b.kind === 'shape'
                      ? ''
                      : 'border-slate-200 hover:border-slate-300')
              }
              style={{
                left: moving ? drag!.x : b.x,
                top: moving ? drag!.y : b.y,
                width: b.width,
                zIndex: b.z_index,
              }}
              onMouseDownCapture={() => focusBlock(b.id)}
            >
              <div
                className="flex h-5 cursor-move items-center justify-between rounded-t bg-slate-50 px-1 opacity-0 group-hover:opacity-100"
                onMouseDown={(e) => startDrag(e, b)}
              >
                <span className="text-[10px] capitalize text-slate-400">{b.kind}</span>
                <button
                  className="text-[10px] text-slate-300 hover:text-red-500"
                  title="Delete block"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => removeBlock(b.id)}
                >
                  ✕
                </button>
              </div>

              {b.kind === 'shape' ? (
                <ShapeBlock
                  body={parseBody<ShapeBlockBody>(b.body_json, { shape: 'rect' })}
                  onChange={(body) => setBody(b.id, body)}
                />
              ) : b.kind === 'file' ? (
                <FileBlock body={parseBody<FileBlockBody>(b.body_json, { asset_id: 0, filename: '', mime: '', size_bytes: 0 })} />
              ) : b.kind === 'checklist' ? (
                <ChecklistBlock
                  body={parseBody<ChecklistBlockBody>(b.body_json, { items: [] })}
                  onChange={(body) => setBody(b.id, body)}
                />
              ) : b.kind === 'table' ? (
                <TableBlock
                  body={parseBody<TableBlockBody>(b.body_json, { rows: [['']], header: true })}
                  onChange={(body) => setBody(b.id, body)}
                  sel={focusId === b.id ? cellSel : null}
                  onSelectCell={(s) => {
                    focusBlock(b.id)
                    setCellSel(s)
                  }}
                  filtersOn={tableFilters[b.id] === true}
                  onRichFocus={setRichFocused}
                />
              ) : (
                <TextBlock
                  html={textHtml(b.body_json)}
                  onChange={(html) => setBody(b.id, { html })}
                  onRichFocus={(f) => {
                    if (f) focusBlock(b.id)
                    setRichFocused(f)
                  }}
                />
              )}

              <span
                className="absolute -right-0.5 bottom-0 top-5 w-1.5 cursor-col-resize opacity-0 hover:bg-slate-300 group-hover:opacity-100"
                onMouseDown={(e) => startResize(e, b)}
                aria-hidden
              />
            </div>
          )
        })}
      </div>
      </div>

    </div>
  )
}

/**
 * One formatting bar for the whole page, showing controls for whatever is
 * selected. Keeping it here rather than inside each block means the canvas
 * stays quiet and the bar never shifts blocks around as it appears.
 *
 * Rich-text commands work without knowing which block is focused:
 * execCommand acts on the document's selection, and the resulting DOM change
 * fires an input event that the block persists itself. The bar only has to
 * avoid stealing focus — hence preventDefault on mousedown.
 */
function FormatBar({
  block,
  richFocused,
  cell,
  filtersOn,
  onToggleFilters,
  onChangeBody,
}: {
  block: NoteBlock | null
  richFocused: boolean
  cell: { r: number; c: number } | null
  filtersOn: boolean
  onToggleFilters: (v: boolean) => void
  onChangeBody: (body: unknown) => void
}) {
  const hold = (e: React.MouseEvent) => e.preventDefault()
  const exec = (cmd: string, value?: string) => document.execCommand(cmd, false, value)

  const kind = block?.kind
  const showRich = richFocused
  const showTable = kind === 'table'
  const showShape = kind === 'shape'

  if (!showRich && !showTable && !showShape) {
    return <div className="h-8" aria-hidden />
  }

  const tableBody = showTable
    ? parseBody<TableBlockBody>(block!.body_json, { rows: [['']], header: true })
    : null
  const shapeBody = showShape ? parseBody<ShapeBlockBody>(block!.body_json, { shape: 'rect' }) : null

  const rows = tableBody?.rows?.length ? tableBody.rows : [['']]
  const cols = Math.max(...rows.map((r) => r.length), 1)
  const styleFor = (r: number, c: number): CellStyle => tableBody?.styles?.[`${r}:${c}`] ?? {}
  const restyle = (patch: CellStyle) => {
    if (!tableBody || !cell) return
    const key = `${cell.r}:${cell.c}`
    onChangeBody({
      ...tableBody,
      styles: { ...(tableBody.styles ?? {}), [key]: { ...styleFor(cell.r, cell.c), ...patch } },
    })
  }

  return (
    <div
      className="flex min-h-8 flex-wrap items-center gap-0.5 rounded-md border border-slate-200 bg-white px-1 py-1"
      onMouseDown={hold}
    >
      {showRich && (
        <>
          <FmtButton label="B" title="Bold" bold onClick={() => exec('bold')} />
          <FmtButton label="I" title="Italic" italic onClick={() => exec('italic')} />
          <FmtButton label="U" title="Underline" underline onClick={() => exec('underline')} />
          <FmtButton label="•" title="Bulleted list" onClick={() => exec('insertUnorderedList')} />
          <FmtButton label="1." title="Numbered list" onClick={() => exec('insertOrderedList')} />
          <FmtButton label="⯇" title="Align left" onClick={() => exec('justifyLeft')} />
          <FmtButton label="≡" title="Align centre" onClick={() => exec('justifyCenter')} />
          <FmtButton label="⯈" title="Align right" onClick={() => exec('justifyRight')} />
          <FmtButton label="☰" title="Justify" onClick={() => exec('justifyFull')} />
          <PopMenu
            trigger="A▾"
            title="Text size"
            items={[
              { label: 'Small', onPick: () => exec('fontSize', '2') },
              { label: 'Normal', onPick: () => exec('fontSize', '3') },
              { label: 'Large', onPick: () => exec('fontSize', '5') },
              { label: 'Huge', onPick: () => exec('fontSize', '6') },
            ]}
          />
          <ColorPicker title="Text colour" swatch="A" colors={TEXT_COLORS} onPick={(c) => exec('foreColor', c)} />
          <ColorPicker
            title="Highlight"
            swatch="▉"
            colors={FILL_COLORS}
            onPick={(c) => exec('hiliteColor', c === 'transparent' ? '#ffffff00' : c)}
          />
          <FmtButton label="⌫" title="Clear formatting" onClick={() => exec('removeFormat')} />
        </>
      )}

      {showTable && tableBody && (
        <>
          {showRich && <span className="mx-1 h-4 w-px bg-slate-200" />}
          {cell && (
            <>
              <FmtButton
                label="B"
                title="Bold cell"
                bold
                onClick={() => restyle({ bold: !styleFor(cell.r, cell.c).bold })}
              />
              <FmtButton label="⯇" title="Align left" onClick={() => restyle({ align: 'left' })} />
              <FmtButton label="≡" title="Align centre" onClick={() => restyle({ align: 'center' })} />
              <FmtButton label="⯈" title="Align right" onClick={() => restyle({ align: 'right' })} />
              <ColorPicker title="Cell text colour" swatch="A" colors={TEXT_COLORS} onPick={(color) => restyle({ color })} />
              <ColorPicker title="Cell fill" swatch="▉" colors={FILL_COLORS} onPick={(bg) => restyle({ bg })} />
              <span className="mx-1 h-4 w-px bg-slate-200" />
            </>
          )}
          <FmtButton
            label="+row"
            title="Add row"
            onClick={() => onChangeBody({ ...tableBody, rows: [...rows, Array(cols).fill('')] })}
          />
          <FmtButton
            label="+col"
            title="Add column"
            onClick={() => onChangeBody({ ...tableBody, rows: rows.map((r) => [...r, '']) })}
          />
          <FmtButton
            label="−row"
            title="Remove last row"
            onClick={() => rows.length > 1 && onChangeBody({ ...tableBody, rows: rows.slice(0, -1) })}
          />
          <FmtButton
            label="−col"
            title="Remove last column"
            onClick={() => cols > 1 && onChangeBody({ ...tableBody, rows: rows.map((r) => r.slice(0, -1)) })}
          />
          <PopMenu
            trigger="▦"
            title="Table options"
            items={[
              { label: 'Borders: all', onPick: () => onChangeBody({ ...tableBody, border: 'all' }) },
              { label: 'Borders: rows', onPick: () => onChangeBody({ ...tableBody, border: 'horizontal' }) },
              { label: 'Borders: none', onPick: () => onChangeBody({ ...tableBody, border: 'none' }) },
              { label: tableBody.wrap ? '✓ Wrap text' : 'Wrap text', onPick: () => onChangeBody({ ...tableBody, wrap: !tableBody.wrap }) },
              {
                label: tableBody.header !== false ? '✓ Header row' : 'Header row',
                onPick: () => onChangeBody({ ...tableBody, header: tableBody.header === false }),
              },
              ...(tableBody.header !== false
                ? [{ label: filtersOn ? '✓ Column filters' : 'Column filters', onPick: () => onToggleFilters(!filtersOn) }]
                : []),
              { label: 'Reset column & row sizes', onPick: () => onChangeBody({ ...tableBody, colWidths: [], rowHeights: [] }) },
            ]}
          />
        </>
      )}

      {showShape && shapeBody && (
        <>
          <ColorPicker title="Fill" swatch="▉" colors={FILL_COLORS} onPick={(v) => onChangeBody({ ...shapeBody, fill: v })} />
          <ColorPicker title="Border" swatch="▭" colors={TEXT_COLORS} onPick={(v) => onChangeBody({ ...shapeBody, stroke: v })} />
          <PopMenu
            trigger="◻▾"
            title="Shape"
            items={[
              { label: 'Rectangle', onPick: () => onChangeBody({ ...shapeBody, shape: 'rect' }) },
              { label: 'Ellipse', onPick: () => onChangeBody({ ...shapeBody, shape: 'ellipse' }) },
              { label: 'Divider', onPick: () => onChangeBody({ ...shapeBody, shape: 'line' }) },
            ]}
          />
        </>
      )}
    </div>
  )
}

/** Page-level actions that aren't worth permanent chrome: where the page
 *  lives, who it belongs to, and archiving. */
function PageMenu({
  note,
  clients,
  allSections,
  onSetClient,
  onMoveSection,
  onArchive,
}: {
  note: Note
  clients: Client[]
  allSections: NoteSection[]
  onSetClient: (clientId: number | null) => void
  onMoveSection: (sectionId: number) => void
  onArchive: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', away)
    return () => window.removeEventListener('mousedown', away)
  }, [open])

  const sectionLabel = (s: NoteSection) =>
    `${s.client_id == null ? 'General' : clients.find((c) => c.id === s.client_id)?.simple_name ?? '?'} / ${s.name}`

  return (
    <div className="relative" ref={ref}>
      <button
        className="rounded px-1.5 text-sm leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        title="Page options"
        aria-label="Page options"
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-52 rounded-md border border-slate-200 bg-white py-1 text-xs shadow-lg">
          <label className="block px-3 py-1.5 text-slate-700">
            Client
            <select
              className="mt-1 w-full rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px]"
              value={note.client_id == null ? '' : String(note.client_id)}
              onChange={(e) => onSetClient(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">General</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.simple_name}
                </option>
              ))}
            </select>
          </label>
          <label className="block px-3 py-1.5 text-slate-700">
            Move to section
            <select
              className="mt-1 w-full rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px]"
              value={String(note.section_id)}
              onChange={(e) => {
                setOpen(false)
                onMoveSection(Number(e.target.value))
              }}
            >
              {allSections.map((s) => (
                <option key={s.id} value={s.id}>
                  {sectionLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <div className="my-1 border-t border-slate-100" />
          <button
            className="block w-full px-3 py-1.5 text-left text-red-600 hover:bg-red-50"
            onClick={() => {
              setOpen(false)
              onArchive()
            }}
          >
            Archive page
          </button>
        </div>
      )}
    </div>
  )
}

/** A backdrop shape. Sits behind other blocks so it can group them visually,
 *  and carries its own height since it has no content to size from. */
function ShapeBlock({
  body,
  onChange,
}: {
  body: ShapeBlockBody
  onChange: (b: ShapeBlockBody) => void
}) {
  const height = body.height ?? 160
  const fill = body.fill ?? '#f1f5f9'
  const stroke = body.stroke ?? '#cbd5e1'

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const startY = e.clientY
    const startH = height
    const move = (ev: MouseEvent) => onChange({ ...body, height: Math.max(40, snap(startH + ev.clientY - startY)) })
    const up = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  return (
    <div className="relative" style={{ height }}>
      {body.shape === 'line' ? (
        <div className="absolute inset-x-2" style={{ top: height / 2, borderTop: `2px solid ${stroke}` }} />
      ) : (
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: fill,
            border: `1px solid ${stroke}`,
            borderRadius: body.shape === 'ellipse' ? '50%' : (body.radius ?? 8),
          }}
        />
      )}

      {/* Vertical resize — shapes are the only block whose height is explicit.
          Revealed on block hover, not just handle hover, or it's unfindable. */}
      <span
        className="absolute inset-x-0 bottom-0 h-2 cursor-row-resize opacity-0 group-hover:opacity-100"
        onMouseDown={startResize}
        aria-hidden
      >
        <span className="absolute bottom-0 left-1/2 h-1 w-8 -translate-x-1/2 rounded-full bg-[var(--accent)]/60" />
      </span>
    </div>
  )
}

/** An attachment on the canvas. Clicking opens the file itself — PDFs and
 *  images render in the tab, everything else downloads. */
function FileBlock({ body }: { body: FileBlockBody }) {
  const kb = body.size_bytes ? Math.max(1, Math.round(body.size_bytes / 1024)) : 0
  const size = kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`
  return (
    <a
      href={api.attachmentUrl(body.asset_id)}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 px-2 py-2 hover:bg-slate-50"
      onMouseDown={(e) => e.stopPropagation()}
      title={body.filename}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        className="shrink-0 text-slate-400"
      >
        <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5z" />
        <path d="M9.5 1.5V5H13" />
      </svg>
      <span className="min-w-0">
        <span className="block truncate text-sm text-slate-800 underline decoration-slate-300">
          {body.filename || 'Attachment'}
        </span>
        {kb > 0 && <span className="block text-[11px] text-slate-400">{size}</span>}
      </span>
    </a>
  )
}

/** Desktop insert bar: one labelled icon per element type, sitting directly
 *  above the format bar. There's room for it on a wide screen, and it saves a
 *  click over the "+" menu that phones still use. */
function AddBlockBar({
  onAdd,
  onAttach,
}: {
  onAdd: (kind: NoteBlockKind) => void
  onAttach: (file: File) => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)

  const Item = ({
    label,
    title,
    onClick,
    children,
  }: {
    label: string
    title: string
    onClick: () => void
    children: React.ReactNode
  }) => (
    <button
      className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      title={title}
      onClick={onClick}
    >
      <span className="text-slate-400">{children}</span>
      {label}
    </button>
  )

  const icon = (d: React.ReactNode) => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      {d}
    </svg>
  )

  return (
    <div className="flex flex-wrap items-center gap-0.5 rounded-md border border-slate-200 bg-white px-1 py-1">
      <Item label="Text" title="Add a text block" onClick={() => onAdd('text')}>
        {icon(
          <>
            <path d="M3 3.5h10M8 3.5v9M6 12.5h4" />
          </>,
        )}
      </Item>
      <Item label="Checklist" title="Add a checklist" onClick={() => onAdd('checklist')}>
        {icon(
          <>
            <rect x="2" y="2.5" width="5" height="5" rx="1" />
            <path d="m3 5 1.2 1.2L6.2 4" />
            <path d="M9.5 5h4.5M2 11.5h12" />
          </>,
        )}
      </Item>
      <Item label="Table" title="Add a table" onClick={() => onAdd('table')}>
        {icon(
          <>
            <rect x="2" y="3" width="12" height="10" rx="1" />
            <path d="M2 6.5h12M6.5 6.5V13" />
          </>,
        )}
      </Item>
      <Item label="Shape" title="Add a background shape" onClick={() => onAdd('shape')}>
        {icon(<rect x="2.5" y="3.5" width="11" height="9" rx="2" />)}
      </Item>
      <Item label="Attach" title="Attach a file" onClick={() => fileInput.current?.click()}>
        {icon(
          <>
            <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5z" />
            <path d="M9.5 1.5V5H13" />
          </>,
        )}
      </Item>
      <input
        ref={fileInput}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) onAttach(f)
        }}
      />
    </div>
  )
}

/** Block types behind a single "+", the way a chat composer hides its
 *  attachment options. Keeps the canvas chrome to one control. */
function AddBlockMenu({
  onAdd,
  onAttach,
}: {
  onAdd: (kind: NoteBlockKind) => void
  onAttach: (file: File) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', away)
    return () => window.removeEventListener('mousedown', away)
  }, [open])

  const items: { kind: NoteBlockKind; label: string }[] = [
    { kind: 'text', label: 'Text' },
    { kind: 'checklist', label: 'Checklist' },
    { kind: 'table', label: 'Table' },
    { kind: 'shape', label: 'Shape' },
  ]

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 bg-white text-lg leading-none text-slate-500 hover:bg-slate-50 hover:text-slate-800"
        title="Add a block"
        aria-label="Add a block"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        +
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-40 overflow-hidden rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg">
          {items.map((it) => (
            <button
              key={it.kind}
              className="block w-full px-3 py-1.5 text-left text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setOpen(false)
                onAdd(it.kind)
              }}
            >
              {it.label}
            </button>
          ))}
          <div className="my-1 border-t border-slate-100" />
          <button
            className="block w-full px-3 py-1.5 text-left text-slate-700 hover:bg-slate-50"
            onClick={() => {
              setOpen(false)
              fileInput.current?.click()
            }}
          >
            Attachment…
          </button>
        </div>
      )}
      <input
        ref={fileInput}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = '' // so picking the same file twice still fires
          if (f) onAttach(f)
        }}
      />
    </div>
  )
}

// ── block editors ───────────────────────────────────────────────────────────
// Each is a plain controlled editor. The positioning layer above doesn't care
// what's inside, so a richer editor can replace any of these later without
// touching drag, resize, or persistence.

function TextBlock({
  html,
  onChange,
  onRichFocus,
}: {
  html: string
  onChange: (v: string) => void
  onRichFocus: (focused: boolean) => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  // Only write into the DOM when it genuinely differs, or the caret jumps to
  // the start on every keystroke.
  useEffect(() => {
    const el = ref.current
    if (el && el.innerHTML !== html) el.innerHTML = html
  }, [html])

  // execCommand from the page-level toolbar fires an input event here, so the
  // block persists its own formatting changes without the toolbar knowing
  // anything about which block it is editing.
  const push = () => onChange(sanitizeHtml(ref.current?.innerHTML ?? ''))

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      data-placeholder="Type…"
      className="note-rich min-h-[1.75rem] w-full bg-transparent px-2 py-1.5 text-sm text-slate-800 outline-none"
      onInput={push}
      onFocus={() => onRichFocus(true)}
      onBlur={() => {
        onRichFocus(false)
        push()
      }}
    />
  )
}

function FmtButton({
  label,
  title,
  onClick,
  bold,
  italic,
  underline,
}: {
  label: string
  title: string
  onClick: () => void
  bold?: boolean
  italic?: boolean
  underline?: boolean
}) {
  return (
    <button
      className={
        'h-6 min-w-6 rounded px-1 text-xs text-slate-600 hover:bg-slate-100 ' +
        (bold ? 'font-bold ' : '') +
        (italic ? 'italic ' : '') +
        (underline ? 'underline ' : '')
      }
      title={title}
      onClick={onClick}
    >
      {label}
    </button>
  )
}

/** A dropdown built from buttons rather than a <select>.
 *
 *  A native select steals focus from the contentEditable, which destroys the
 *  selection execCommand needs — the command then applies to a collapsed caret
 *  and appears to do nothing. Buttons inside the toolbar inherit its
 *  mousedown-preventDefault, so the selection survives. */
function PopMenu({
  trigger,
  title,
  items,
}: {
  trigger: string
  title: string
  items: { label: string; onPick: () => void }[]
}) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative">
      <button
        className="h-6 min-w-6 rounded px-1 text-xs text-slate-600 hover:bg-slate-100"
        title={title}
        onClick={() => setOpen((v) => !v)}
      >
        {trigger}
      </button>
      {open && (
        <span className="absolute left-0 top-full z-40 mt-1 flex w-max flex-col rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {items.map((it) => (
            <button
              key={it.label}
              className="px-3 py-1 text-left text-xs text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setOpen(false)
                it.onPick()
              }}
            >
              {it.label}
            </button>
          ))}
        </span>
      )}
    </span>
  )
}

function ColorPicker({
  title,
  swatch,
  colors,
  onPick,
}: {
  title: string
  swatch: string
  colors: { label: string; value: string }[]
  onPick: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative">
      <button
        className="h-6 min-w-6 rounded px-1 text-xs text-slate-600 hover:bg-slate-100"
        title={title}
        onClick={() => setOpen((v) => !v)}
      >
        {swatch}
      </button>
      {open && (
        <span className="absolute left-0 top-full z-40 mt-1 w-max rounded-md border border-slate-200 bg-white p-1.5 shadow-lg">
          <span className="flex gap-1">
            {colors.map((c) => (
              <button
                key={c.value}
                title={c.label}
                className="h-5 w-5 rounded border border-slate-300"
                style={{ backgroundColor: c.value === 'transparent' ? '#fff' : c.value }}
                onClick={() => {
                  setOpen(false)
                  onPick(c.value)
                }}
              />
            ))}
          </span>
          {/* "Other": the OS colour wheel, plus hex for an exact brand value. */}
          <span className="mt-1.5 flex items-center gap-1 border-t border-slate-100 pt-1.5">
            <input
              type="color"
              className="h-5 w-6 cursor-pointer border-0 bg-transparent p-0"
              title="Colour wheel"
              onChange={(e) => onPick(e.target.value)}
            />
            <input
              className="w-20 rounded border border-slate-200 px-1 py-0.5 text-[11px] text-slate-700 outline-none"
              placeholder="#RRGGBB"
              spellCheck={false}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                const v = (e.target as HTMLInputElement).value.trim()
                const hex = v.startsWith('#') ? v : `#${v}`
                if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) {
                  setOpen(false)
                  onPick(hex)
                }
              }}
            />
          </span>
        </span>
      )}
    </span>
  )
}

function ChecklistBlock({
  body,
  onChange,
}: {
  body: ChecklistBlockBody
  onChange: (b: ChecklistBlockBody) => void
}) {
  const items = body.items ?? []
  const refs = useRef<(HTMLInputElement | null)[]>([])

  const update = (next: ChecklistBlockBody['items']) => onChange({ items: next })

  return (
    <div className="px-2 py-1.5">
      <ul className="flex flex-col gap-0.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <input
              type="checkbox"
              className="mt-1"
              checked={it.done}
              onChange={(e) => update(items.map((x, j) => (j === i ? { ...x, done: e.target.checked } : x)))}
            />
            <input
              ref={(el) => {
                refs.current[i] = el
              }}
              className={
                'w-full bg-transparent text-sm outline-none ' +
                (it.done ? 'text-slate-400 line-through' : 'text-slate-800')
              }
              value={it.text}
              placeholder="Item"
              onChange={(e) => update(items.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  const next = [...items]
                  next.splice(i + 1, 0, { text: '', done: false })
                  update(next)
                  // Focus the row we just inserted, so Enter chains like a list.
                  setTimeout(() => refs.current[i + 1]?.focus(), 0)
                } else if (e.key === 'Backspace' && it.text === '' && items.length > 1) {
                  e.preventDefault()
                  update(items.filter((_, j) => j !== i))
                  setTimeout(() => refs.current[Math.max(0, i - 1)]?.focus(), 0)
                }
              }}
            />
          </li>
        ))}
      </ul>
      <button
        className="mt-1 text-xs text-slate-400 hover:text-slate-700"
        onClick={() => update([...items, { text: '', done: false }])}
      >
        + item
      </button>
    </div>
  )
}

function TableBlock({
  body,
  onChange,
  sel,
  onSelectCell,
  filtersOn,
  onRichFocus,
}: {
  body: TableBlockBody
  onChange: (b: TableBlockBody) => void
  sel: { r: number; c: number } | null
  onSelectCell: (s: { r: number; c: number }) => void
  filtersOn: boolean
  onRichFocus: (focused: boolean) => void
}) {
  const rows = body.rows?.length ? body.rows : [['']]
  const cols = Math.max(...rows.map((r) => r.length), 1)
  const header = body.header !== false
  const wrap = body.wrap === true
  const border = body.border ?? 'all'
  const styles = body.styles ?? {}
  const setSel = onSelectCell
  const [sort, setSort] = useState<{ col: number; dir: 'asc' | 'desc' } | null>(null)
  const [filters, setFilters] = useState<Record<number, string>>({})

  // Sorting and filtering are a *view*: `rows` is never reordered. Every
  // rendered row carries its original index, so edits and per-cell styles —
  // which are keyed by position — always land on the right underlying row.
  const collator = useMemo(() => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }), [])
  const display = useMemo(() => {
    const all = rows.map((row, index) => ({ row, index }))
    if (!header) return all // layout tables are never sorted or filtered

    const head = all.slice(0, 1)
    let bodyRows = all.slice(1)

    const active = Object.entries(filters).filter(([, v]) => v.trim() !== '')
    if (filtersOn && active.length) {
      bodyRows = bodyRows.filter(({ row }) =>
        active.every(([c, needle]) => (row[Number(c)] ?? '').toLowerCase().includes(needle.trim().toLowerCase())),
      )
    }
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1
      bodyRows = [...bodyRows].sort(
        (a, b) => collator.compare(a.row[sort.col] ?? '', b.row[sort.col] ?? '') * dir,
      )
    }
    return [...head, ...bodyRows]
  }, [rows, header, filters, filtersOn, sort, collator])

  const toggleSort = (col: number) =>
    setSort((s) => (s?.col !== col ? { col, dir: 'asc' } : s.dir === 'asc' ? { col, dir: 'desc' } : null))

  const colWidths = body.colWidths ?? []
  const rowHeights = body.rowHeights ?? []

  const startColResize = (e: React.MouseEvent, col: number) => {
    e.preventDefault()
    e.stopPropagation()
    const td = (e.currentTarget as HTMLElement).parentElement as HTMLElement
    const startX = e.clientX
    const startW = colWidths[col] ?? td.getBoundingClientRect().width
    const move = (ev: MouseEvent) => {
      const next = [...colWidths]
      while (next.length < cols) next.push(null)
      next[col] = Math.max(48, Math.round(startW + ev.clientX - startX))
      onChange({ ...body, colWidths: next })
    }
    const up = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  const startRowResize = (e: React.MouseEvent, rowIndex: number) => {
    e.preventDefault()
    e.stopPropagation()
    const tr = ((e.currentTarget as HTMLElement).parentElement as HTMLElement).parentElement as HTMLElement
    const startY = e.clientY
    const startH = rowHeights[rowIndex] ?? tr.getBoundingClientRect().height
    const move = (ev: MouseEvent) => {
      const next = [...rowHeights]
      while (next.length < rows.length) next.push(null)
      next[rowIndex] = Math.max(24, Math.round(startH + ev.clientY - startY))
      onChange({ ...body, rowHeights: next })
    }
    const up = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  const setCell = (r: number, col: number, v: string) =>
    onChange({ ...body, rows: rows.map((row, i) => (i === r ? row.map((cell, j) => (j === col ? v : cell)) : row)) })

  const styleFor = (r: number, c: number): CellStyle => styles[`${r}:${c}`] ?? {}

  return (
    <div className="relative px-1 py-1">
      <table
        className={'w-full text-sm ' + (border === 'none' ? '' : 'border-collapse')}
        style={{ tableLayout: colWidths.some((w) => w != null) ? 'fixed' : 'auto' }}
      >
        <colgroup>
          {Array.from({ length: cols }, (_, c) => (
            <col key={c} style={colWidths[c] ? { width: colWidths[c]! } : undefined} />
          ))}
        </colgroup>
        <tbody>
          {/* Filter row, data mode only. */}
          {header && filtersOn && (
            <tr>
              {Array.from({ length: cols }, (_, c) => (
                <td key={c} className={cellBorder(border, true)}>
                  <input
                    className="w-full bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600 outline-none"
                    placeholder="filter…"
                    value={filters[c] ?? ''}
                    onChange={(e) => setFilters({ ...filters, [c]: e.target.value })}
                  />
                </td>
              ))}
            </tr>
          )}

          {display.map(({ row, index }) => (
            <tr key={index} style={rowHeights[index] ? { height: rowHeights[index]! } : undefined}>
              {Array.from({ length: cols }, (_, c) => {
                const st = styleFor(index, c)
                const isHeaderCell = header && index === 0
                const bg = st.bg && st.bg !== 'transparent' ? st.bg : undefined
                return (
                  <td
                    key={c}
                    className={
                      cellBorder(border, false) +
                      ' relative' +
                      // Show which cell the page toolbar is acting on.
                      (sel && sel.r === index && sel.c === c ? ' outline outline-1 outline-[var(--accent)]' : '')
                    }
                    style={{ backgroundColor: bg }}
                  >
                    {/* Drag the right edge to size the column, the bottom edge
                        to size the row. */}
                    <span
                      className="absolute -right-0.5 inset-y-0 z-10 w-1.5 cursor-col-resize hover:bg-[var(--accent)]/40"
                      onMouseDown={(e) => startColResize(e, c)}
                      aria-hidden
                    />
                    <span
                      className="absolute -bottom-0.5 inset-x-0 z-10 h-1.5 cursor-row-resize hover:bg-[var(--accent)]/40"
                      onMouseDown={(e) => startRowResize(e, index)}
                      aria-hidden
                    />
                    {isHeaderCell ? (
                      <div className="flex items-center">
                        <input
                          className="w-full bg-transparent px-1.5 py-1 text-sm font-semibold outline-none focus:bg-slate-50/60"
                          style={{ color: st.color, textAlign: st.align }}
                          value={row[c] ?? ''}
                          onFocus={() => setSel({ r: index, c })}
                          onChange={(e) => setCell(index, c, e.target.value)}
                        />
                        <button
                          className="px-1 text-[10px] text-slate-400 hover:text-slate-700"
                          title={`Sort by ${row[c] || `column ${c + 1}`}`}
                          onClick={() => toggleSort(c)}
                        >
                          {sort?.col === c ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                        </button>
                      </div>
                    ) : header ? (
                      // Data mode: plain text. Wrap uses a textarea because an
                      // <input> cannot wrap.
                      wrap ? (
                        <AutoGrow
                          value={row[c] ?? ''}
                          style={{ color: st.color, textAlign: st.align }}
                          bold={st.bold}
                          onFocus={() => setSel({ r: index, c })}
                          onChange={(v) => setCell(index, c, v)}
                        />
                      ) : (
                        <input
                          className={
                            'w-full bg-transparent px-1.5 py-1 text-sm outline-none focus:bg-slate-50/60 ' +
                            (st.bold ? 'font-semibold' : '')
                          }
                          style={{ color: st.color, textAlign: st.align }}
                          value={row[c] ?? ''}
                          onFocus={() => setSel({ r: index, c })}
                          onChange={(e) => setCell(index, c, e.target.value)}
                        />
                      )
                    ) : (
                      // Layout mode: rich text per cell.
                      <CellRichText
                        html={row[c] ?? ''}
                        wrap={wrap}
                        style={{ color: st.color, textAlign: st.align }}
                        bold={st.bold}
                        onFocus={() => {
                          setSel({ r: index, c })
                          onRichFocus(true)
                        }}
                        onBlur={() => onRichFocus(false)}
                        onChange={(v) => setCell(index, c, v)}
                      />
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {header && (sort || Object.values(filters).some(Boolean)) && (
        <div className="mt-0.5 text-[11px] text-slate-400">
          View only — your row order is unchanged.{' '}
          <button
            className="underline hover:text-slate-600"
            onClick={() => {
              setSort(null)
              setFilters({})
            }}
          >
            Reset
          </button>
        </div>
      )}
    </div>
  )
}

/** Border classes per style. Kept out of the JSX so the table stays readable. */
function cellBorder(style: TableBlockBody['border'], isFilterRow: boolean): string {
  const base = 'p-0 '
  if (style === 'none') return base
  if (style === 'horizontal') return base + (isFilterRow ? 'border-b border-slate-200' : 'border-b border-slate-200')
  return base + 'border border-slate-200'
}

/** Textarea that grows with its content — used for wrapped data cells. */
function AutoGrow({
  value,
  onChange,
  onFocus,
  style,
  bold,
}: {
  value: string
  onChange: (v: string) => void
  onFocus: () => void
  style?: React.CSSProperties
  bold?: boolean
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }, [value])
  return (
    <textarea
      ref={ref}
      rows={1}
      className={
        'w-full resize-none overflow-hidden bg-transparent px-1.5 py-1 text-sm outline-none focus:bg-slate-50/60 ' +
        (bold ? 'font-semibold' : '')
      }
      style={style}
      value={value}
      onFocus={onFocus}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/** Rich-text cell for layout tables. Same sanitising as a text block. */
function CellRichText({
  html,
  onChange,
  onFocus,
  onBlur,
  wrap,
  style,
  bold,
}: {
  html: string
  onChange: (v: string) => void
  onFocus: () => void
  onBlur: () => void
  wrap: boolean
  style?: React.CSSProperties
  bold?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (el && el.innerHTML !== html) el.innerHTML = html
  }, [html])
  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className={
        'note-rich min-h-[1.5rem] w-full bg-transparent px-1.5 py-1 text-sm outline-none focus:bg-slate-50/60 ' +
        (bold ? 'font-semibold ' : '') +
        (wrap ? '' : 'whitespace-nowrap overflow-x-auto')
      }
      style={style}
      onFocus={onFocus}
      onInput={() => onChange(sanitizeHtml(ref.current?.innerHTML ?? ''))}
      onBlur={() => {
        onBlur()
        onChange(sanitizeHtml(ref.current?.innerHTML ?? ''))
      }}
    />
  )
}

// ── archive ─────────────────────────────────────────────────────────────────

function ArchiveModal({ onClose, onRestored }: { onClose: () => void; onRestored: () => void }) {
  const [items, setItems] = useState<Note[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api
      .listNotes({ archived: true })
      .then(setItems)
      .catch((e) => pushError(`Couldn't load archive: ${(e as Error).message}`))
      .finally(() => setLoading(false))
  }, [])

  async function restore(n: Note) {
    setItems((is) => is.filter((x) => x.id !== n.id))
    await api.updateNote(n.id, { archived: 0 })
    onRestored()
  }

  async function destroy(n: Note) {
    if (!confirm(`Permanently delete "${n.title || 'Untitled'}"? This cannot be undone.`)) return
    setItems((is) => is.filter((x) => x.id !== n.id))
    await api.deleteNote(n.id)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div
        className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="font-semibold text-slate-900">Archived pages</h2>
          <button className="text-slate-400 hover:text-slate-700" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400">Loading…</p>
          ) : items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400">Nothing archived.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((n) => (
                <li key={n.id} className="flex items-center gap-3 px-4 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-800">{n.title || 'Untitled'}</div>
                    <div className="truncate text-xs text-slate-400">
                      archived {n.updated_at.slice(0, 10)} · {n.body_text.slice(0, 80) || 'empty'}
                    </div>
                  </div>
                  <button className="text-xs text-[var(--accent)] hover:underline" onClick={() => restore(n)}>
                    Restore
                  </button>
                  <button className="text-xs text-red-600 hover:underline" onClick={() => destroy(n)}>
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
